import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DownloadIcon, QrCode } from 'components';
import { BANK_STYLES } from 'constants/bank-styles';
import { CLARITY_MASK_PROPS } from 'constants/clarity.constants';
import { useSqToast } from 'context';
import { TranslationNamespace } from 'i18n';
import { saveImage } from 'utils';

import { SqLogoImage } from '../SqLogoImage';

interface Props {
  data?: string;
  imageUrl?: string;
  bank?: string;
}

export const SqQrCode: React.FC<Props> = ({ data, imageUrl, bank }) => {
  const { t: tPayment } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });
  const { t: tToast } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.sq_toast',
  });
  const { showToast } = useSqToast();

  const [generatedQrUrl, setGeneratedQrUrl] = useState<string>();

  const bankName = useMemo(
    () =>
      bank
        ? BANK_STYLES[bank as keyof typeof BANK_STYLES]?.name || bank
        : undefined,
    [bank],
  );

  const downloadUrl = useMemo(
    () => imageUrl ?? generatedQrUrl,
    [imageUrl, generatedQrUrl],
  );

  const handleDownload = useCallback(async () => {
    if (!downloadUrl) {
      return;
    }
    await saveImage(downloadUrl, 'qr.png');
    showToast(tToast('qr_downloaded'));
  }, [downloadUrl, showToast, tToast]);

  return (
    <>
      <div className="tw-text-sm tw-text-secondary tw-mb-1 tw-pl-4">
        {tPayment('qr_code.placeholder')}
      </div>
      <div className="tw-bg-payment-layout-out tw-rounded-[24px] tw-px-4 tw-py-3">
        <div className="tw-relative tw-mx-auto tw-w-72 tw-h-72">
          {imageUrl ? (
            <img
              className="tw-w-full tw-h-full"
              src={imageUrl}
              alt={tPayment('qr_code.placeholder')}
              {...CLARITY_MASK_PROPS}
            />
          ) : data ? (
            <QrCode
              data={data}
              className="tw-w-full tw-h-full"
              innerClassName="tw-w-full tw-h-full"
              imageClassName="tw-w-full tw-h-full"
              onGenerated={setGeneratedQrUrl}
            />
          ) : null}
          {(imageUrl || data) && (
            <div className="tw-bg-white tw-p-1 tw-rounded-full tw-z-[4] tw-absolute tw-top-1/2 tw-left-1/2 tw-transform -tw-translate-x-1/2 -tw-translate-y-1/2">
              <SqLogoImage
                bank={bank}
                bankName={bankName}
                hideDefault
                className="tw-w-12 tw-h-12 tw-shrink-0"
              />
            </div>
          )}
        </div>
        {downloadUrl && (
          <button
            onClick={handleDownload}
            data-testid="sq-qr-download-button"
            className="tw-mt-3 tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-gray-200 tw-bg-white tw-py-3 tw-text-sm tw-font-medium tw-text-primary tw-cursor-pointer"
          >
            <DownloadIcon />
            {tPayment('qr_code.download')}
          </button>
        )}
      </div>
    </>
  );
};
